import { useCallback, useEffect, useState } from "react";
import { ArrowsLeftRight, WarningCircle } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WealthShell } from "@/pages/viveka/wealth/WealthShell";
import { BUCKET_TONE, CR, cr, pct } from "@/lib/wealth";
import { toast } from "sonner";

const INPUTS = [
  ["sustainable_fcf", "Sustainable free cash flow", "What the business reliably generates"],
  ["growth_capex", "Less: growth capex", "Reinvestment the business needs to grow"],
  ["required_cash_reserve", "Less: required business cash reserve", "Working capital buffer kept inside"],
];
const DECISION = [
  ["plough_back", "Plough back into the business"],
  ["pull_out", "Pull out to family wealth"],
];
const ALLOC = [
  ["allocation_safety", "safety", "Safety"],
  ["allocation_market", "market", "Market"],
  ["allocation_aspirational", "aspirational", "Aspirational"],
];

const num = (v) => (v == null || v === "" ? "" : v / CR);
const cls = "mt-2 w-full rounded-xl border border-border bg-card px-3 py-2 text-right font-mono text-sm text-navy outline-none focus:border-plum disabled:opacity-60";

export default function WealthBridge() {
  const { tenantId } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const { data: d } = await api.get(`/tenants/${tenantId}/wealth/bridge`);
      setData(d);
      setForm(Object.fromEntries(Object.entries(d.inputs).map(([k, v]) =>
        [k, typeof v === "number" ? num(v) : (v || "")])));
    } catch (e) { toast.error(errMsg(e)); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const crVal = (k) => (Number(form[k]) || 0) * CR;

  const save = async () => {
    try {
      const { data: d } = await api.put(`/tenants/${tenantId}/wealth/bridge`, {
        period_label: form.period_label || "",
        notes: form.notes || "",
        sustainable_fcf: crVal("sustainable_fcf"),
        growth_capex: crVal("growth_capex"),
        required_cash_reserve: crVal("required_cash_reserve"),
        plough_back: crVal("plough_back"),
        pull_out: crVal("pull_out"),
        allocation_safety: crVal("allocation_safety"),
        allocation_market: crVal("allocation_market"),
        allocation_aspirational: crVal("allocation_aspirational"),
      });
      setData({ ...d, can_edit: data?.can_edit });
      toast.success("Scenario saved — nothing is committed to the register until Board approval");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const canEdit = data?.can_edit;
  const available = ((Number(form.sustainable_fcf) || 0) - (Number(form.growth_capex) || 0)
    - (Number(form.required_cash_reserve) || 0));
  const allocated = ALLOC.reduce((s, [k]) => s + (Number(form[k]) || 0), 0);
  const pullOut = Number(form.pull_out) || 0;

  return (
    <WealthShell
      title="Business to family wealth bridge"
      description="How business cash generation becomes family wealth: cash available, plough back versus pull out, and the proposed position against the Family Board's desired allocation. This is a scenario — the register only changes when the Board approves the transfer."
    >
      {!data ? <p className="label-caps animate-pulse">Loading bridge</p> : (
        <div className="space-y-6" data-testid="wealth-bridge">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.1fr_1fr]">
            <div className="panel p-6" data-testid="bridge-cash">
              <p className="label-caps">Step 1 · Potential cash available (₹ Cr)</p>
              <div className="mt-4 space-y-4">
                {INPUTS.map(([k, label, hint]) => (
                  <label key={k} className="block">
                    <span className="text-sm font-medium text-navy">{label}</span>
                    <span className="block text-[11px] text-muted-foreground">{hint}</span>
                    <input type="number" step="any" min="0" value={form[k] ?? ""} disabled={!canEdit}
                           data-testid={`bridge-${k}`} onChange={(e) => set(k, e.target.value)}
                           className={cls} />
                  </label>
                ))}
              </div>
              <div className="mt-5 flex items-center justify-between rounded-xl bg-teal/10 px-5 py-4">
                <p className="text-sm font-semibold text-navy">Potential cash available</p>
                <p className="font-mono text-xl font-semibold tabular-nums text-teal"
                   data-testid="bridge-available">₹{available.toLocaleString("en-IN", { maximumFractionDigits: 4 })} Cr</p>
              </div>
            </div>

            <div className="panel p-6" data-testid="bridge-decision">
              <p className="label-caps">Step 2 · Capital allocation decision (₹ Cr)</p>
              <div className="mt-4 space-y-4">
                {DECISION.map(([k, label]) => (
                  <label key={k} className="block">
                    <span className="text-sm font-medium text-navy">{label}</span>
                    <input type="number" step="any" min="0" value={form[k] ?? ""} disabled={!canEdit}
                           data-testid={`bridge-${k}`} onChange={(e) => set(k, e.target.value)}
                           className={cls} />
                  </label>
                ))}
              </div>
              <p className="mt-4 text-[11px] text-muted-foreground">
                Plough back + pull out must equal the potential cash available.
              </p>

              <p className="label-caps mt-6">Step 3 · Allocate the pull-out to buckets (₹ Cr)</p>
              <div className="mt-3 grid grid-cols-3 gap-3">
                {ALLOC.map(([k, bucket, label]) => (
                  <label key={k} className="block">
                    <span className={`inline-block rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] ${BUCKET_TONE[bucket].chip}`}>
                      {label}
                    </span>
                    <input type="number" step="any" min="0" value={form[k] ?? ""} disabled={!canEdit}
                           data-testid={`bridge-${k}`} onChange={(e) => set(k, e.target.value)}
                           className={cls} />
                  </label>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between rounded-xl bg-muted/50 px-5 py-3">
                <p className="text-sm text-navy">Allocated</p>
                <p className={`font-mono text-sm font-semibold tabular-nums ${
                  Math.abs(allocated - pullOut) < 0.0001 ? "text-success" : "text-destructive"}`}
                   data-testid="bridge-allocated">
                  ₹{allocated.toLocaleString("en-IN", { maximumFractionDigits: 4 })} Cr of ₹{pullOut.toLocaleString("en-IN", { maximumFractionDigits: 4 })} Cr
                </p>
              </div>
              {canEdit && (
                <button onClick={save} data-testid="bridge-save"
                        className="mt-5 rounded-full bg-plum px-6 py-3 text-[10px] uppercase tracking-[0.16em] text-white hover:bg-plum/90">
                  Save scenario & recompute
                </button>
              )}
            </div>
          </div>

          {data.warnings?.length > 0 && (
            <div className="flex items-start gap-3 rounded-2xl border-l-4 border-warning bg-warning/10 px-5 py-4"
                 data-testid="bridge-warnings">
              <WarningCircle size={20} className="mt-0.5 shrink-0 text-[hsl(28_82%_38%)]" />
              <ul className="space-y-1 text-sm text-[hsl(28_82%_30%)]">
                {data.warnings.map((w) => <li key={w}>{w}</li>)}
              </ul>
            </div>
          )}

          <div className="panel overflow-x-auto" data-testid="bridge-proposed">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-4">
              <div className="flex items-center gap-2">
                <ArrowsLeftRight size={18} className="text-plum" />
                <h3 className="font-display text-lg text-navy">Proposed risk allocation after pull-out</h3>
              </div>
              <p className="font-mono text-sm tabular-nums text-navy">
                Net worth {cr(data.current_net_worth)} → <span className="font-semibold">{cr(data.proposed_net_worth)}</span>
              </p>
            </div>
            <table className="w-full min-w-[46rem] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  {["Bucket", "Current value", "Current %", "New allocation", "Proposed value",
                    "Proposed %", "Desired %", "Gap now", "Gap after"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.rows.map((r) => (
                  <tr key={r.bucket} data-testid={`bridge-row-${r.bucket}`}>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] ${BUCKET_TONE[r.bucket].chip}`}>
                        {r.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-foreground/80">{cr(r.current_value)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-foreground/80">{pct(r.current_pct)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-teal">
                      {r.new_allocation ? `+ ${cr(r.new_allocation)}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold tabular-nums text-navy">{cr(r.proposed_value)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold tabular-nums text-navy">{pct(r.proposed_pct)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-foreground/70">{pct(r.desired_pct)}</td>
                    <td className={`px-4 py-3 text-right font-mono tabular-nums ${
                      (r.current_gap_pct ?? 0) === 0 ? "text-muted-foreground"
                        : (r.current_gap_pct ?? 0) > 0 ? "text-[hsl(28_82%_38%)]" : "text-destructive"}`}>
                      {r.current_gap_pct == null ? "—"
                        : `${r.current_gap_pct > 0 ? "+" : ""}${r.current_gap_pct.toFixed(2)}%`}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono font-semibold tabular-nums ${
                      r.proposed_gap_pct == null ? "text-muted-foreground"
                        : Math.abs(r.proposed_gap_pct) <= Math.abs(r.current_gap_pct ?? 99)
                          ? "text-success" : "text-destructive"}`}>
                      {r.proposed_gap_pct == null ? "—"
                        : `${r.proposed_gap_pct > 0 ? "+" : ""}${r.proposed_gap_pct.toFixed(2)}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="px-6 py-3 text-[11px] text-muted-foreground">
              Desired % comes from the Family Board policy set on the Wealth allocator screen.
              Nothing here changes the asset register — the transfer is committed only on Board approval.
            </p>
          </div>
        </div>
      )}
    </WealthShell>
  );
}
